import { __ } from '@/hub-shared/utils/i18n';

export type ImportFactRow = {
  metric_key?: string;
  period_basis?: string;
  completeness_status?: string;
  source_slug?: string;
  value?: { confidence?: string; review_needed?: boolean };
};

export type ImportOutcomeSummary = {
  monthlyCount: number;
  annualCount: number;
  reviewNeededMetricKeys: string[];
  factLines: string[];
};

/** Expected while building a 12-month DSN set — not a failed import. */
export const DSN_SOFT_BLOCK_REASON_CODES = [
  'dsn_set_incomplete_annual_policy',
  'annual_period_basis_required',
] as const;

export type BlockedHint = { metric_key?: string; reason_code?: string; message?: string };

export const hasHardImportBlock = (blocked?: BlockedHint[] | null): boolean => {
  if (!blocked?.length) {
    return false;
  }
  return blocked.some(b => {
    const code = b.reason_code ?? '';
    return code !== '' && !DSN_SOFT_BLOCK_REASON_CODES.includes(code as (typeof DSN_SOFT_BLOCK_REASON_CODES)[number]);
  });
};

const METRIC_LABELS: Record<string, string> = {
  'fec.turnover.pcg': __('Chiffre d’affaires (FEC)'),
  'fec.balance_sheet_total.pcg': __('Total bilan (FEC)'),
  'fec.resource_spend.v1': __('Dépenses énergie / eau (FEC)'),
  // Legacy metric — no longer written, kept only so an older import's stored row still
  // displays a label. See docs/quality/migrations/2026-08-25-fec-environmental-estimate-removal.md.
  'fec.environmental_estimate.v1': __('Estimation environnementale (FEC, ancienne version)'),
  'fec.ledger.summary': __('Résumé FEC'),
  'dsn.active_headcount': __('Effectif moyen (DSN annuel)'),
  'dsn.operation_countries.snapshot': __('Pays d’exploitation (DSN)'),
  'dsn.establishments.snapshot': __('Établissements (DSN)'),
  'dsn.workforce_distribution.snapshot': __('Répartition effectifs (DSN mensuel)'),
  'dsn.workforce.distributions.annual': __('Répartition effectifs (DSN annuel)'),
  'dsn.turnover_rate.annual': __('Taux de rotation (DSN annuel)'),
  'dsn.gender_pay_gap.annual': __('Écart de rémunération (DSN annuel)'),
  'dsn.minimum_monthly_salary.annual': __('Salaire minimum observé (DSN annuel)'),
  'dsn.collective_agreement_coverage.snapshot': __('Conventions collectives (DSN mensuel)'),
  'dsn.collective_agreement_coverage.annual': __('Conventions collectives (DSN annuel)'),
};

export const labelForMetric = (metricKey: string): string => METRIC_LABELS[metricKey] ?? metricKey;

const needsReview = (row: ImportFactRow): boolean => {
  if (row.completeness_status === 'partial') {
    return true;
  }
  const value = row.value;
  if (!value || typeof value !== 'object') {
    return false;
  }
  return value.confidence === 'estimated' || value.review_needed === true;
};

export const summarizeImportOutcome = (
  monthlyFacts: unknown[] | undefined,
  annualFacts: unknown[] | undefined,
): ImportOutcomeSummary => {
  const monthly = (monthlyFacts ?? []).filter((r): r is ImportFactRow => typeof r === 'object' && r !== null);
  const annual = (annualFacts ?? []).filter((r): r is ImportFactRow => typeof r === 'object' && r !== null);
  const all = [...monthly, ...annual];

  const reviewNeededMetricKeys = all
    .filter(needsReview)
    .map(r => (typeof r.metric_key === 'string' ? r.metric_key : ''))
    .filter(k => k !== '');

  const factLines = all
    .filter(r => typeof r.metric_key === 'string' && r.metric_key !== 'fec.ledger.summary')
    .map(r => {
      const key = r.metric_key ?? '';
      let line = labelForMetric(key);
      if (r.completeness_status === 'partial') {
        line += ` (${__('données partielles')})`;
      }
      if (needsReview(r)) {
        line += ` (${__('à valider')})`;
      }
      return line;
    });

  return {
    monthlyCount: monthly.length,
    annualCount: annual.length,
    reviewNeededMetricKeys,
    factLines,
  };
};
